/**
 * Phase 140A — Scoreboard overlay component.
 *
 * Spectator-side, lives on top of the LiveKit video frame in <LivexWatch>.
 * Renders home/away team identity, sport-aware score, period and live timer.
 *
 * Props:
 *   scoreboard: see /api/livex/streams/{id}/live-state response shape
 *   className:  extra positioning classes from the caller (e.g. absolute top-3 left-3)
 *
 * Visual posture: dark glassmorphism so it never washes out against
 * grass/court/floor footage. No external dependencies.
 */
import { useEffect, useState } from 'react';

function _fmtIntScore(score) {
  if (typeof score === 'number') return String(score);
  return '0';
}

function _fmtCricketScore(score) {
  const s = score || {};
  const overs = (s.overs || 0).toString();
  return (
    <span className="tabular-nums">
      <span className="text-2xl font-black">{s.runs ?? 0}</span>
      <span className="text-base text-white/70">/{s.wickets ?? 0}</span>
      <span className="text-[10px] text-white/50 ml-1">({overs})</span>
    </span>
  );
}

function _fmtSetPointScore(score) {
  const s = score || {};
  return (
    <span className="tabular-nums">
      <span className="text-2xl font-black">{s.points ?? 0}</span>
      <span className="text-[10px] text-white/50 ml-1">(set {s.sets ?? 0})</span>
    </span>
  );
}

function ScorePill({ score, shape }) {
  if (shape === 'cricket') return _fmtCricketScore(score);
  if (shape === 'set_point') return _fmtSetPointScore(score);
  return <span className="text-2xl font-black tabular-nums">{_fmtIntScore(score)}</span>;
}

function TeamRow({ side, shape, accent }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5" data-testid={`scoreboard-${accent}`}>
      <div className="flex items-center gap-2 min-w-0">
        {side?.logo_url ? (
          <img src={side.logo_url} alt="" className="h-6 w-6 rounded object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div
            className="h-6 w-6 rounded grid place-items-center text-[10px] font-black text-white"
            style={{ background: side?.color || '#475569' }}
          >
            {(side?.name || '?').slice(0, 1).toUpperCase()}
          </div>
        )}
        <span className="text-sm font-bold text-white truncate max-w-[140px]">
          {side?.name || (accent === 'home' ? 'Home' : 'Away')}
        </span>
      </div>
      <ScorePill score={side?.score} shape={shape} />
    </div>
  );
}

function useLiveTimer(timer) {
  // Compute the elapsed-ms string INSIDE the interval callback (not during
  // render) so the hook stays pure for react-hooks/purity. The state below
  // is the only render-visible side of the live clock.
  const [display, setDisplay] = useState('00:00');
  useEffect(() => {
    function compute() {
      if (!timer) { setDisplay('00:00'); return; }
      let elapsedMs = Number(timer.offset_ms || 0);
      if (timer.running && timer.started_at_ms) {
        elapsedMs += Date.now() - Number(timer.started_at_ms);
      }
      const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
      const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const ss = String(totalSeconds % 60).padStart(2, '0');
      setDisplay(`${mm}:${ss}`);
    }
    compute();
    if (!timer?.running) return undefined;
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [timer]);
  return display;
}

export default function ScoreboardOverlay({ scoreboard, className = '' }) {
  const sb = scoreboard;
  const elapsed = useLiveTimer(sb?.timer);
  if (!sb || sb.enabled === false) return null;

  const shape = sb.score_shape || 'int';
  const showTimer = sb?.timer && sb.timer.direction !== 'off';

  return (
    <div
      className={`pointer-events-none ${className}`}
      data-testid="scoreboard-overlay"
    >
      <div
        className="rounded-xl border border-white/10 shadow-lg backdrop-blur-md px-3 py-2 min-w-[220px]"
        style={{ background: 'rgba(8, 11, 22, 0.78)' }}
      >
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] font-bold text-white/70 mb-1">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" /> Live
          </span>
          <span className="flex items-center gap-2">
            {sb.period && <span className="text-white/90">{sb.period}</span>}
            {showTimer && elapsed && <span className="tabular-nums text-white/90 font-mono">{elapsed}</span>}
          </span>
        </div>
        <TeamRow side={sb.home} shape={shape} accent="home" />
        <div className="h-px bg-white/10 my-0.5" />
        <TeamRow side={sb.away} shape={shape} accent="away" />
      </div>
    </div>
  );
}
